/* eslint-disable @typescript-eslint/no-explicit-any */
import { differenceInYears, parse, isValid, isAfter } from 'date-fns';
import reduce from 'lodash/reduce';
import size from 'lodash/size';
import trim from 'lodash/trim';

/**
 * Perform validation of each element in `values` and returns a new object containing only the keys for which the
 * `validation` did not pass, with `message` as value.
 *
 * @param {Object} values An object where keys are form field names and values are their associated form field values.
 * @param {Function} validation A validation function to execute on each element of `values`.
 * @param {String} message The error message to set as value of each element of the returned object.
 * @returns {Object} An object containing only keys for which validation did not pass and with `message` as values.
 */

export const getValidationErrors = <T>(
  values: { [key: string]: T },
  validation: (argument: T) => boolean,
  message: string,
): { [key: string]: string } =>
  reduce(
    values,
    (errors, value, key) => {
      if (!validation(value)) {
        return { ...errors, [key]: message };
      }

      return errors;
    },
    {},
  );

/**
 * Checks if value is not empty.
 *
 * @param {String} [value] A string to check for emptyness.
 * @returns {Boolean}
 */
export const isRequiredValid = (value: string): boolean =>
  size(trim(value)) > 0;

/**
 * Checks if a date is a valid date in a given format.
 *
 * @param {String} value The value to validate.
 * @param {String} format Date format string.
 * @returns {Boolean}
 */
export const isDateValid = (value: string, format: string): boolean => {
  // https://github.com/date-fns/date-fns/issues/942
  if (value.length !== format.length) {
    return false;
  }
  const asDate = parse(value, format, new Date());
  return isValid(asDate);
};

/**
 * Checks if a date is a valid birth date.
 *
 * @param {String} date The date to validate.
 * @param {String} format Date format string.
 * @returns {Boolean}
 */
export const isValidBirthDate = (date: string, format: string): boolean => {
  if (!isDateValid(date, format)) {
    return false;
  }

  const birthDate = parse(date, format, new Date());

  if (differenceInYears(new Date(), birthDate) < 18) {
    return false;
  }

  const minDate = new Date('1900-01-01');
  return isAfter(birthDate, minDate);
};

export const isInUnion = <T>(union: T[], value: any): value is T =>
  union.includes(value);
